import i18n from 'i18next'
import { StringConverter } from '@portal/pkg-filter-criteria'

const MAGNITUDE_EXPONENTS: { [suffix: string]: number } = {
  m: 6, // millions   → ×1,000,000
  b: 9, // billions   → ×1,000,000,000
}

/**
 * A {@link StringConverter} that understands a trailing magnitude shorthand
 * suffix a user types into a numeric or currency field:
 *   - `m` (millions)  → value × 1,000,000     e.g. `5m`   → 5,000,000
 *   - `b` (billions)  → value × 1,000,000,000 e.g. `5.5b` → 5,500,000,000
 * The suffix is accepted in both lower and upper case.
 *
 * A bare suffix with no numeric part in front of it (`m`, `b`) is left as-is so
 * that it fails validation (see {@link StringConverter.isValidStrict}).
 *
 * The multiplication is done by shifting the decimal point in the string rather
 * than multiplying floats, so exact results like `4.1b` → 4,100,000,000 are not
 * corrupted by floating-point error (e.g. `4.1 * 1e9 === 4099999999.9999995`).
 *
 * This is only used for values a user enters through the application. Values
 * arriving through any other channel (e.g. ingestion) never pass through here,
 * so the shorthand is not applied to them.
 */
export default class MagnitudeStringConverter extends StringConverter {
  private readonly exponent: number

  constructor(value: string) {
    const trimmed = value.trim()
    const suffix = trimmed.slice(-1).toLowerCase()
    const exponent = MAGNITUDE_EXPONENTS[suffix]
    const numericPart = trimmed.slice(0, -1)

    // Only treat the trailing character as a magnitude suffix when there is an
    // actual number in front of it, so a bare `m`/`b` remains invalid.
    if (exponent && /\d/.test(numericPart)) {
      super(numericPart)
      this.exponent = exponent
    } else {
      super(value)
      this.exponent = 0
    }
  }

  public hasMagnitudeSuffix(): boolean {
    return this.exponent !== 0
  }

  public toNumber(locale: string = i18n.language): number {
    if (this.exponent === 0) {
      return super.toNumber(locale)
    }
    const normalized = this.valueAsNumericStringFor(locale)
    return parseFloat(MagnitudeStringConverter.shiftDecimalRight(normalized, this.exponent))
  }

  // Moves the decimal point `places` digits to the right without floating-point
  // arithmetic. e.g. ('4.1', 9) -> '4100000000', ('5.5', 6) -> '5500000'.
  private static shiftDecimalRight(numeric: string, places: number): string {
    const negative = numeric.startsWith('-')
    const unsigned = negative ? numeric.slice(1) : numeric
    const [intPartRaw, fracPartRaw = ''] = unsigned.split('.')
    const intPart = intPartRaw || '0'

    const result =
      fracPartRaw.length <= places
        ? intPart + fracPartRaw + '0'.repeat(places - fracPartRaw.length)
        : intPart + fracPartRaw.slice(0, places) + '.' + fracPartRaw.slice(places)

    return negative ? `-${result}` : result
  }
}
